import {
  ActionIcon,
  Tooltip,
  Paper,
  TextInput,
  Textarea,
  Button,
  Stack,
  Text,
  CloseButton,
  Box,
} from "@mantine/core";
import { IconMessage } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { isRecipePath } from "../../utils/routeHelpers";

type FloatingContactButtonProps = {
  /** Force-hide the collapsed button (an already-opened form stays visible). */
  hidden?: boolean;
};

export const FloatingContactButton = ({ hidden = false }: FloatingContactButtonProps) => {
  const [opened, setOpened] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [visible, setVisible] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [formData, setFormData] = useState({
    name: user?.displayName || "",
    email: user?.email || "",
    message: "",
  });

  // Hide on recipe pages for unauthenticated users (CTA banner takes that space)
  if (!user && isRecipePath(location.pathname)) return null;

  const shown = opened || (visible && !hidden);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.message) {
      return;
    }

    // For logged-out users, validate email
    if (!user?.email) {
      if (!formData.email || !/^\S+@\S+$/.test(formData.email)) {
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/support/contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: user?.email || formData.email,
            subject: "Contact Form Question",
            message: formData.message,
            type: "general",
            userId: user?.uid,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setFormData({
        name: user?.displayName || "",
        email: user?.email || "",
        message: "",
      });
      setOpened(false);
    } catch (error) {
      console.error("Error sending contact form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      // The class lets index.css hide the FAB while a scroll-locking overlay
      // (any drawer/modal) is open on mobile — see the rule there.
      className="floating-contact-fab"
      style={{
        position: "fixed",
        bottom: "24px",
        right: "24px",
        zIndex: 1000,
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 0.3s ease, transform 0.3s ease",
        pointerEvents: shown ? "auto" : "none",
      }}
    >
      {opened && (
        <Paper
          shadow="lg"
          p="md"
          radius="lg"
          style={{
            position: "absolute",
            bottom: "80px",
            right: "0",
            width: "320px",
            maxWidth: "calc(100vw - 48px)",
          }}
        >
          <Stack gap="sm">
            <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Text fw={600} size="lg">
                Ask a question
              </Text>
              <CloseButton onClick={() => setOpened(false)} />
            </Box>

            <form onSubmit={handleSubmit}>
              <Stack gap="sm">
                <TextInput
                  label="Name"
                  placeholder="Your name"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />

                {!user?.email && (
                  <TextInput
                    label="Email"
                    placeholder="your.email@example.com"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                )}

                <Textarea
                  label="Message"
                  placeholder="What's your question?"
                  required
                  minRows={3}
                  maxRows={6}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                />

                <Button
                  type="submit"
                  loading={submitting}
                  fullWidth
                  variant="gradient"
                  gradient={{ deg: 45, from: 'orange', to: 'red' }}
                >
                  Send message
                </Button>
              </Stack>
            </form>
          </Stack>
        </Paper>
      )}

      <Tooltip label="Contact us" position="left" disabled={opened}>
        <ActionIcon
          size="xl"
          radius="xl"
          variant="filled"
          color="blue"
          onClick={() => setOpened(!opened)}
          style={{
            width: "60px",
            height: "60px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
        >
          <IconMessage size={28} />
        </ActionIcon>
      </Tooltip>
    </Box>
  );
};
